import { useDispatch, useSelector } from "react-redux";
import { Button, DatePicker, TextInput } from "../../components";
import { AppDispatch, RootState } from "../../redux/store";
import { useContext, useEffect, useState } from "react";
import Stepper, { StepperState } from "../../components/Stepper";
import { Add, DeleteOutline } from "@mui/icons-material";
import { PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { colors } from "../../utils/constants";
import { SelectOption } from "../../components/Select/SelectNew";
import RadioInputNew from "../../components/RadioInput/RadioInputNew";
import SidebarFooter from "../../components/SidebarFooter";
import { ToastContext } from "../../contexts/ToastContext/ToastContext";
import {
  completePatientSubscription,
  getOfficeAnnualPlans,
  getPaymentUpgrade,
  getReceiptData,
  getSubscriptionPaymentIntent,
  getUpdateSubscriptionPaymentIntent,
  getValidateSubscribeUpdate,
} from "../../redux/slices/annualPlanSlice";
import { OfficeAnnualPlan, PlanNames, SubscriptionType } from "../../models/annual-plan.model";
import { customOrder } from "../OfficePlan/planData";
import { formatCurrency, getPlanDetails } from "../../utils";
import StripeElement from "../../components/StripeElement";
import { UserAnnualPlan } from "../../models/user-annual-plan.model";
import { uploadedInvoice, uploadFileNew } from "../../redux/slices/FileSlice";
import { uploadStripeReceipt } from "../../redux/slices/annualPlanSlice";
import { DsFile } from "../../models/ds-file.model";
import PlanReceiptPdf, { PlanReceiptData } from "../../components/forms/Receipt/PlanReceiptPdf";
import { getUser } from "../../redux/slices/CurrentUserSlice";
const html2pdf = require("html2pdf.js");
export interface FamilyRow {
  firstName: string;
  lastName: string;
  dob: string;
}

const PaymentForm = ({ handleComplete, previous }: { handleComplete: (paymentId: string) => void; previous: () => void }) => {
  const toast = useContext(ToastContext);
  const [loading, setLoading] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const stripe = useStripe();
  const elements = useElements();
  const submit = async () => {
    if (stripe && elements) {
      setLoading(true);
      const { error, paymentIntent: confirmIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: "if_required",
      });
      if ((confirmIntent?.status === "succeeded" || confirmIntent?.status === "processing") && confirmIntent?.payment_method) {
        await handleComplete(confirmIntent.payment_method.toString());
      } else {
        toast?.errorToast(error?.message ?? "Payment failed");
      }
      setLoading(false);
    }
  };
  return (
    <>
      <p className="py-2 text-semibold">
        <b>Credit Card Information</b>
      </p>
      <PaymentElement
        className="stripe-payment-element"
        id={"Payment-Form-Stripe"}
        onChange={(e: any) => {
          setIsDisabled(e.complete);
        }}
      />
      <SidebarFooter
        cancelText={"Previous"}
        saveText={"Submit"}
        isDisabledNext={!isDisabled || loading}
        loading={loading}
        isDisabledPrev={loading}
        handleClose={previous}
        handleSave={submit}
      />
    </>
  );
};

const PaymentMethodStep = ({
  selectedPlan,
  paymentIntent,
  handleComplete,
  previous,
  additionalInfo,
  planDetails,
  handleUpgradePlan,
  loading,
}: {
  selectedPlan: OfficeAnnualPlan;
  paymentIntent: any;
  handleComplete: (paymentId: string) => void;
  previous: () => void;
  additionalInfo?: any;
  planDetails?: any;
  handleUpgradePlan?: () => Promise<void>;
  loading?: boolean;
}) => {
  const cleanedPrice = Number(planDetails?.price?.replace("$", "").replace(".", ""));
  const intentPrice = additionalInfo?.updatedPlan?.officeAnnualPlan?.subscription?.price - cleanedPrice;
  const currentUser = useSelector((state: RootState) => state.currentUser.currentUser);
  const currentOffice = currentUser?.offices?.length ? currentUser?.offices[0] : undefined;
  const upgradeAmount = additionalInfo?.proratedAmount;
  const packageFee = upgradeAmount ? upgradeAmount : additionalInfo?.updatedPlan?.officeAnnualPlan ? intentPrice : selectedPlan.subscription.price;
  const docnoteValue = packageFee * 0.1;
  const hstEnables = currentOffice?.taxEnabled ? packageFee + docnoteValue : docnoteValue;
  const hstAmount = (hstEnables * 0.13) / 100;
  const hstAmountRounded = hstAmount.toFixed(2); // 0.429 -> "0.43"
  const hstAmountRoundedStr = (Math.round((hstAmount + Number.EPSILON) * 100) / 100).toFixed(2);
  const total = currentOffice?.taxEnabled
    ? packageFee + docnoteValue + (packageFee + docnoteValue) * 0.13
    : packageFee + docnoteValue + docnoteValue * 0.13;
  const finalTotal = (total / 100).toFixed(2);
  const amountFromStripe = paymentIntent?.amount;
  const SelectedPlanText =
    selectedPlan.annualPlanType.subscriptionType === "monthly" ? "monthly for a term of twelve months" : "once for a term of twelve months";
  return (
    <>
      <p>
        This subscription will be charged {SelectedPlanText} to the below credit card. Please note that this subscription cannot be cancelled within
        the 12-month term. You may update your payment method at any time.
      </p>
      <ul className="mt-5">
        <li className="flex gap-5 mb-2">
          <p className="w-1/2">Annual Plan:</p>
          <p>{PlanNames[selectedPlan.annualPlanType.name as keyof typeof PlanNames]}</p>
        </li>
        <li className="flex gap-5 mb-2">
          <p className="w-1/2 capitalize">{selectedPlan.annualPlanType.subscriptionType} Fee:</p>
          <p>{formatCurrency((packageFee / 100).toString(), "blur")}</p>
        </li>
        <li className="flex gap-5 mb-2">
          <p className="w-1/2 capitalize">Docnote Fee (10%):</p>
          <p>{formatCurrency((docnoteValue / 100).toString(), "blur")}</p>
        </li>
        <li className="flex gap-5 mb-2">
          <p className="w-1/2 capitalize">HST (13%):</p>
          {/* <p>{formatCurrency(((hstEnables * 0.13) / 100).toString(), "blur")}</p> */}
          <p>{formatCurrency(hstAmountRoundedStr, "blur")}</p>
        </li>
        <li className="flex gap-5 font-bold mb-2">
          <p className="w-1/2 capitalize">Total {selectedPlan.annualPlanType.subscriptionType} Fee:</p>
          <p>{formatCurrency((amountFromStripe / 100).toString(), "blur")}</p>
        </li>
      </ul>
      {handleUpgradePlan && (
        <div className="flex gap-3 mt-3">
          <Button text="Cancel" onClick={previous} disabled={loading} />
          <Button text="Confirm" onClick={handleUpgradePlan} disabled={loading} />
        </div>
      )}
      <div className="w-full">
        {paymentIntent?.clientSecret ? (
          <StripeElement clientSecret={paymentIntent?.clientSecret}>
            <PaymentForm handleComplete={handleComplete} previous={previous} />
          </StripeElement>
        ) : null}
      </div>
    </>
  );
};

export const FamilyMemberStep = ({
  addMember,
  removeMember,
  familyRows,
}: {
  familyRows: FamilyRow[];
  addMember: (firstName: string, lastName: string, dob: string) => void;
  removeMember: (ind: number) => void;
}) => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [dob, setDob] = useState("");

  const handleAddMember = async () => {
    await addMember(firstName, lastName, dob);
    setFirstName("");
    setLastName("");
    setDob("");
  };
  return (
    <>
      <p>
        Please enter the names and birth dates of any additional family members (only registered patients of this doctor that live at the same address
        can be included). Please do not include your own name; the plan automatically lists the purchaser as the account holder.
      </p>
      <div className="flex flex-col gap-3 mt-2">
        <div>
          <TextInput
            label="First Name"
            placeholder="Enter First Name"
            value={firstName}
            onChangeFunc={val => {
              const sanitizedValue = val.target.value.replace(/[$|]/g, "");
              setFirstName(sanitizedValue);
            }}
          />
        </div>
        <div>
          <TextInput
            label="Last Name"
            placeholder="Enter Last Name"
            value={lastName}
            onChangeFunc={val => {
              const sanitizedValue = val.target.value.replace(/[$|]/g, "");
              setLastName(sanitizedValue);
            }}
          />
        </div>
        <div className="relative">
          <DatePicker
            label={"Date of Birth"}
            isRequired={false}
            name={"dob"}
            placeholder={"Date of Birth"}
            value={dob}
            onChange={val => {
              const sanitizedValue = val.replace(/[$|]/g, "");
              setDob(sanitizedValue);
            }}
            errorMsg={""}
            isError={false}
            changeHeight={true}
          />
        </div>
      </div>
      <div className="flex justify-center mt-5">
        <Button
          disabled={!firstName.trim().length || !lastName.trim().length || !dob.trim().length}
          icon={<Add fontSize="small" className="text-xs" />}
          width="fit"
          text="Add Family Member"
          onClick={handleAddMember}
        />
      </div>

      {familyRows.length > 0 ? (
        <div className="mt-5">
          <h2 className="font-bold mb-2">Family Members</h2>
          {familyRows.map((row, index) => {
            return (
              <div key={index} className={`grid grid-cols-2 gap-5 border-b py-2`}>
                <p className="capitalize">
                  {row.firstName} {row.lastName}
                </p>
                <div className="flex justify-between">
                  <p>{row.dob}</p>
                  <p className="text-right cursor-pointer">
                    <DeleteOutline htmlColor={colors.Orange} onClick={() => removeMember(index)} />
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      ) : null}
    </>
  );
};

const PlanSelection = ({
  officeId,
  currentPlan,
  isEdit = false,
  handleClose,
}: {
  officeId: string;
  currentPlan?: UserAnnualPlan;
  isEdit?: boolean;
  handleClose: (val: boolean) => void;
}) => {
  const toast = useContext(ToastContext);
  const dispatch = useDispatch<AppDispatch>();
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedPlan, setSelectedPlan] = useState("");
  const [selectedPaymentFrequency, setSelectedPaymentFrequency] = useState("");
  const [familyRows, setFamilyRows] = useState<FamilyRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [paymentIntent, setPaymentIntent] = useState<any>(undefined);
  const [receiptData, setReceiptData] = useState<PlanReceiptData | undefined>(undefined);
  const [savingPdf, setSavingPdf] = useState(false);
  const [fileToUpload, setFileToUpload] = useState<File | undefined>(undefined);
  const [shouldCloseAfterUpload, setShouldCloseAfterUpload] = useState(false);
  const [userAnnualPlanIdForReceipt, setUserAnnualPlanIdForReceipt] = useState<string | undefined>(undefined);
  const [confirmationRequired, setConfirmationRequired] = useState(false);
  const [manualConfirmationPending, setManualConfirmationPending] = useState(false);
  const [manualConfirmationMessage, setManualConfirmationMessage] = useState<string | null>(null);
  const [pendingAdditionalInfo, setPendingAdditionalInfo] = useState<string | undefined>(undefined);
  const [pendingStep, setPendingStep] = useState<number | null>(null);
  const { annualPlans } = useSelector((state: RootState) => state.annualPlan);
  const { currentUser } = useSelector((state: RootState) => state.currentUser);
  const showStep3 = !["Senior", "Single"].includes(selectedPlan);
  const showSubmit = (showStep3 && currentStep === 4) || (!showStep3 && currentStep === 3);

  const paymentFrequency = annualPlans
    .filter(item => item.annualPlanType.name === selectedPlan)
    .map(item => {
      const planName = PlanNames[item.annualPlanType.name as keyof typeof PlanNames];
      const price = formatCurrency((item.subscription.price / 100).toString(), "blur");
      return {
        label: `${planName} ${price}/${item.subscription.subscriptionType}`,
        value: item.officeAnnualPlanId,
      };
    });

  let isDisabled = false;

  if (currentStep === 1) {
    isDisabled = !selectedPlan;
  } else if (currentStep === 2) {
    isDisabled = !selectedPaymentFrequency;
  } else if (showStep3 && currentStep === 3 && familyRows.length === 0) {
    isDisabled = true;
  }

  const addMember = (firstName: string, lastName: string, dob: string) => {
    const isFamily = selectedPlan.toLocaleLowerCase() === "family";
    const familyCount = isFamily ? 6 : 2;
    if (showStep3 && familyRows.length >= (isFamily ? familyCount : 1)) {
      toast?.openToast(`You can only have ${familyCount} family members with a ${selectedPlan.toLocaleLowerCase()} plan.`);
      return;
    }
    setFamilyRows([...familyRows, { firstName, lastName, dob }]);
  };

  const removeMember = (index: number) => {
    const updatedRows = [...familyRows];
    updatedRows.splice(index, 1);
    setFamilyRows(updatedRows);
  };

  const getSaveText = () => {
    if (isEdit) {
      if (showStep3 && currentStep === 3) return "Update Annual Plan";
      if (currentStep === 2 && !showStep3) return "Update Annual Plan";
      return "Next";
    }
    return showSubmit ? "Submit" : "Next";
  };

  const handleCancelAction = () => {
    if (currentStep === 1) {
      handleClose(false); // Close the modal
    } else {
      setConfirmationRequired(false);
      setCurrentStep(currentStep - 1); // Go to the previous step
    }
  };
  const planDetails = getPlanDetails(currentUser?.userAnnualPlan?.officeAnnualPlan);

  const handleSaveAction = async () => {
    const stripeSubscriptionId = annualPlans.find(item => item.officeAnnualPlanId === selectedPaymentFrequency)?.subscription.subscriptionId;
    if (isEdit) {
      if (currentPlan?.officeAnnualPlan?.subscription.subscriptionId === stripeSubscriptionId && currentStep === 2) {
        toast?.errorToast("Looks like nothing is changed.");
        return;
      }
    }

    if (isEdit && ((showStep3 && currentStep === 3) || (currentStep === 2 && !showStep3))) {
      toast?.openToast();
      handleClose(true);
    } else {
      const step = currentStep + 1;
      const additionalInfo = familyRows?.length > 0 ? familyRows.map(item => `${item.firstName}|${item.lastName}|${item.dob}`).join("$") : undefined;

      // Cache values so that we can reuse them when user manually confirms
      setPendingAdditionalInfo(additionalInfo);
      setPendingStep(step);

      if (currentUser?.userId && ((step === 3 && !showStep3) || (step === 4 && showStep3))) {
        setLoading(true);
        let res;
        const userID = currentUser.userId;

        if (!planDetails) {
          res = await dispatch(
            getSubscriptionPaymentIntent({
              officeId,
              planId: selectedPaymentFrequency,
              userId: userID,
              additionalMembers: additionalInfo,
            })
          );
        } else {
          const validateRes = await dispatch(
            getValidateSubscribeUpdate({
              officeId,
              planId: currentUser?.userAnnualPlan ? currentUser?.userAnnualPlan?.officeAnnualPlan?.officeAnnualPlanId : "",
              userId: userID,
              newPlanID: selectedPaymentFrequency,
              additionalMembers: additionalInfo,
            })
          );

          // If API indicates that confirmation is required, show message + buttons instead of proceeding immediately
          if (validateRes?.payload?.confirmationRequired) {
            setManualConfirmationPending(true);
            setManualConfirmationMessage(validateRes?.payload?.statusMessage || "Confirm payment to complete plan change.");
            setLoading(false);
            return;
          }

          // No manual confirmation required – proceed with existing flow
          res = await dispatch(
            getUpdateSubscriptionPaymentIntent({
              officeId,
              planId: currentUser?.userAnnualPlan ? currentUser?.userAnnualPlan?.officeAnnualPlan?.officeAnnualPlanId : "",
              userId: userID,
              newPlanID: selectedPaymentFrequency,
              additionalMembers: additionalInfo,
            })
          );
          if (res?.payload?.success === false) {
            toast?.errorToast(res?.payload?.data?.message);
            handleClose(true);
          } else {
            if (!(res?.payload?.paymentIntent && res?.payload?.paymentIntent?.length)) {
              toast?.openToast(res?.payload?.statusMessage);
              if (res?.payload?.confirmationRequired) {
                setConfirmationRequired(true);
                // Advance to payment step
                const paymentStep = showStep3 ? 4 : 3;
                setCurrentStep(paymentStep);
              } else {
                handleClose(true);
              }
            }
          }
        }
        setLoading(false);
        setPaymentIntent(res.payload);
      }
      setCurrentStep(step); // Move to the next step
    }
  };

  const handleChangePlan = (val: PlanNames) => {
    setSelectedPlan(val);
    setSelectedPaymentFrequency("");
    setFamilyRows([]);
    setConfirmationRequired(false);
  };

  const handleComplete = async (paymentId: string) => {
    const stripeSubscriptionId = annualPlans.find(item => item.officeAnnualPlanId === selectedPaymentFrequency)?.subscription.subscriptionId;
    if (currentUser?.userId && stripeSubscriptionId) {
      setConfirmationRequired(false);
      const res = await dispatch(
        completePatientSubscription({
          officeId,
          userId: currentUser?.userId,
          planId: selectedPaymentFrequency,
          stripeSubscriptionId: stripeSubscriptionId,
          paymentId,
        })
      );
      if (!res?.payload) {
        toast?.errorToast(`Annual plan ${isEdit ? "edit" : "active"} failed.`);
      }
      if (res?.payload?.userAnnualPlanState === "active") {
        if (officeId && currentUser?.userId && res?.payload?.userAnnualPlanId) {
          setUserAnnualPlanIdForReceipt(res?.payload?.userAnnualPlanId);
          const ReceiptData = await dispatch(
            getReceiptData({
              officeId,
              userId: currentUser?.userId,
              userAnnualPlanId: res?.payload?.userAnnualPlanId,
            })
          );
          if (ReceiptData?.payload) {
            setReceiptData(ReceiptData.payload as PlanReceiptData);
            setShouldCloseAfterUpload(true);
          } else {
            // If ReceiptData fails, close anyway
            handleClose(true);
          }
        } else {
          // If required params missing, close anyway
          handleClose(true);
        }
      } else {
        // If subscription not active, close anyway
        handleClose(true);
      }
      // Close is deferred until after PDF generation/upload completes
    }
  };

  const handleUpgradePlan = async () => {
    if (currentUser?.userId && officeId) {
      setLoading(true);
      try {
        const upgradeData = await dispatch(
          getPaymentUpgrade({
            officeId,
            userId: currentUser.userId,
            proratedAmount: paymentIntent?.proratedAmount,
            proratedDate: paymentIntent?.proratedDate,
          })
        );
        if (upgradeData?.payload) {
          toast?.openToast("Plan upgraded successfully");
          handleClose(true);
          dispatch(getUser());
        } else {
          toast?.errorToast("Failed to upgrade plan");
        }
      } catch (error) {
        toast?.errorToast("Failed to upgrade plan");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleManualConfirmationCancel = () => {
    setManualConfirmationPending(false);
    setManualConfirmationMessage(null);
    setPendingAdditionalInfo(undefined);
    setPendingStep(null);
  };

  const handleManualConfirmationConfirm = async () => {
    if (!currentUser?.userId || !officeId || !selectedPaymentFrequency) {
      handleManualConfirmationCancel();
      return;
    }

    const userID = currentUser.userId;
    const additionalInfo =
      pendingAdditionalInfo ??
      (familyRows?.length > 0 ? familyRows.map(item => `${item.firstName}|${item.lastName}|${item.dob}`).join("$") : undefined);
    const nextStep = pendingStep ?? currentStep + 1;

    try {
      setLoading(true);
      const res: any = await dispatch(
        getUpdateSubscriptionPaymentIntent({
          officeId,
          planId: currentUser?.userAnnualPlan ? currentUser?.userAnnualPlan?.officeAnnualPlan?.officeAnnualPlanId : "",
          userId: userID,
          newPlanID: selectedPaymentFrequency,
          additionalMembers: additionalInfo,
        })
      );

      if (res?.payload?.success === false) {
        toast?.errorToast(res?.payload?.data?.message);
        handleClose(true);
      } else {
        if (!(res?.payload?.paymentIntent && res?.payload?.paymentIntent?.length)) {
          toast?.openToast(res?.payload?.statusMessage);
          if (res?.payload?.confirmationRequired) {
            setConfirmationRequired(true);
            const paymentStep = showStep3 ? 4 : 3;
            setCurrentStep(paymentStep);
          } else {
            handleClose(true);
            dispatch(getUser());
          }
        } else {
          setPaymentIntent(res.payload);
          setCurrentStep(nextStep);
        }
      }
    } finally {
      setLoading(false);
      setManualConfirmationPending(false);
      setManualConfirmationMessage(null);
      setPendingAdditionalInfo(undefined);
      setPendingStep(null);
    }
  };

  useEffect(() => {
    dispatch(getOfficeAnnualPlans({ officeId }));
  }, [officeId]);

  useEffect(() => {
    if (currentPlan) {
      setSelectedPlan(currentPlan.officeAnnualPlan.annualPlanType.name);
      setSelectedPaymentFrequency(currentPlan.officeAnnualPlan.officeAnnualPlanId);
      const familyMembers = currentUser?.userAnnualPlan?.additionalMembers
        ? currentUser?.userAnnualPlan?.additionalMembers.split("$").map(item => {
            const [firstName, lastName, dob] = item.split("|");
            return { firstName, lastName, dob };
          })
        : [];
      setFamilyRows(familyMembers);
    }
  }, [currentPlan]);

  const plans = annualPlans?.length
    ? annualPlans
        .filter(item => item.subscription.subscriptionType === SubscriptionType.Monthly && item?.isActive)
        .reduce((acc: SelectOption[], curr) => {
          const planName = PlanNames[curr.annualPlanType.name as keyof typeof PlanNames];
          acc.push({
            label: planName,
            value: curr.annualPlanType.name,
            IsActive: curr?.isActive,
          });
          return acc;
        }, [])
    : [];

  plans.sort((a: SelectOption, b: SelectOption) => customOrder.indexOf(a.label) - customOrder.indexOf(b.label));

  const plan = annualPlans.find(item => item.officeAnnualPlanId === selectedPaymentFrequency);

  // Helper function to get the correct payment intent
  const getPaymentIntentData = () => {
    // When confirmationRequired is true, payment intent might be structured differently
    if (confirmationRequired) {
      // Check if paymentIntent is directly in the payload
      if (paymentIntent?.paymentIntent) {
        return Array.isArray(paymentIntent.paymentIntent) ? paymentIntent.paymentIntent[0] : paymentIntent.paymentIntent;
      }
      // If paymentIntent is not in the expected structure, return the whole paymentIntent object
      // as it might contain the payment intent data directly
      return paymentIntent;
    }
    if (!planDetails) {
      return paymentIntent?.paymentIntent;
    }
    return paymentIntent?.paymentIntent?.[0];
  };

  const getPaymentStepComponent = () => {
    if (!plan) return null;

    // Show payment step if we're at submit step OR if confirmation is required
    if (!showSubmit && !confirmationRequired) return null;

    // When confirmationRequired is true, show PaymentMethodStep
    if (confirmationRequired) {
      const paymentIntentFromData = getPaymentIntentData();
      return (
        <PaymentMethodStep
          selectedPlan={plan}
          paymentIntent={paymentIntentFromData}
          handleComplete={handleComplete}
          previous={handleCancelAction}
          additionalInfo={paymentIntent}
          planDetails={planDetails}
          handleUpgradePlan={handleUpgradePlan}
          loading={loading}
        />
      );
    }

    // Normal flow: check for payment intent data
    const paymentIntentData = paymentIntent?.paymentIntent;
    if (!paymentIntentData) return null;

    const hasClientSecret = !!paymentIntentData.clientSecret;
    const hasPaymentIntentArray = Array.isArray(paymentIntentData) && paymentIntentData.length > 0;

    if (hasClientSecret || hasPaymentIntentArray) {
      return (
        <PaymentMethodStep
          selectedPlan={plan}
          paymentIntent={getPaymentIntentData()}
          handleComplete={handleComplete}
          previous={handleCancelAction}
          additionalInfo={paymentIntent}
          planDetails={planDetails}
        />
      );
    }

    return null;
  };
  const paymentStepComponent = getPaymentStepComponent();

  // Generate receipt PDF after setting receiptData
  useEffect(() => {
    const generateReceiptPdf = async () => {
      if (!receiptData) return;
      setSavingPdf(true);
      const element = document.getElementById("plan-receipt-container-pdf");
      if (!element) {
        setSavingPdf(false);
        return;
      }
      // Ensure the element is visible for html2pdf to capture
      const wasHidden = element.classList.contains("hidden");
      if (wasHidden) element.classList.remove("hidden");
      // Wait a frame to allow layout/paint
      await new Promise(requestAnimationFrame);
      const opt = {
        margin: [5, 0, 5, 0],
        filename: `DocnoteReceipt-${receiptData.invoiceNumber}`,
        image: { type: "jpeg", quality: 1 },
        html2canvas: { scale: 4, dpi: 192, letterRendering: true, useCORS: true },
        jsPDF: { unit: "mm", format: "a4", compress: true, orientation: "portrait" },
        pagebreak: { mode: "avoid-all" },
      } as any;
      try {
        const arrayBuffer: ArrayBuffer = await html2pdf().set(opt).from(element).outputPdf("arraybuffer");
        const file = new File([arrayBuffer], `DocnoteReceipt-${receiptData.invoiceNumber}.pdf`, {
          type: "application/pdf",
        });
        setFileToUpload(file);
      } catch (e) {
        setSavingPdf(false);
      } finally {
        // Restore visibility state
        if (wasHidden) element.classList.add("hidden");
      }
    };
    generateReceiptPdf();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receiptData]);

  // Upload the generated file
  useEffect(() => {
    if (!fileToUpload) return;
    (async () => {
      const action = await dispatch(
        uploadFileNew({
          file: fileToUpload,
          callback: undefined,
        })
      );
      const uploaded = action?.payload as DsFile;
      const fileId = uploaded?.fileId;
      if (fileId) {
        dispatch(uploadedInvoice(true));
        // Attach the uploaded file as a Stripe receipt
        if (currentUser?.userId && userAnnualPlanIdForReceipt) {
          await dispatch(
            uploadStripeReceipt({
              fileID: fileId,
              userId: currentUser.userId,
              userAnnualPlanId: userAnnualPlanIdForReceipt,
            })
          );
        }
        if (shouldCloseAfterUpload) {
          setShouldCloseAfterUpload(false);
          handleClose(true);
        }
      }
      setSavingPdf(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileToUpload]);

  return (
    <div className="relative px-8">
      <div className={`grid mb-5 ${showStep3 ? (isEdit ? "grid-cols-3" : "grid-cols-4") : isEdit ? "grid-cols-2" : "grid-cols-3"}`}>
        <Stepper activeState={currentStep === 1 ? (isEdit ? StepperState.Full : StepperState.Half) : StepperState.Full}>
          <span>
            Select
            <br />
            Plan Type
          </span>
        </Stepper>

        <Stepper
          activeState={currentStep === 2 ? (isEdit ? StepperState.Full : StepperState.Half) : currentStep < 2 ? StepperState.None : StepperState.Full}
        >
          <span>
            Payment
            <br />
            Frequency
          </span>
        </Stepper>

        {showStep3 && (
          <Stepper activeState={currentStep === 3 ? StepperState.Half : currentStep < 3 ? StepperState.None : StepperState.Full}>
            <span>
              Add Family
              <br />
              Members
            </span>
          </Stepper>
        )}

        {!isEdit && (
          <Stepper activeState={showSubmit ? StepperState.Full : currentStep < 4 ? StepperState.None : StepperState.Half}>
            <span>Payment</span>
          </Stepper>
        )}
      </div>

      {/* Step 1 */}
      {currentStep === 1 ? (
        <>
          <p className="mb-5">
            The following annual plan options are offered by your doctor’s office. Please select your preferred option and click Proceed To Payment to
            subscribe.
          </p>
          <RadioInputNew onChangeFunc={val => handleChangePlan(val as PlanNames)} value={selectedPlan} options={plans} />
        </>
      ) : null}
      {/* Step 2 */}
      {currentStep === 2 && !manualConfirmationPending ? (
        <>
          <p className="mb-5">Your annual plan is available in a monthly or yearly payment schedule. Please select your preference.</p>
          <RadioInputNew onChangeFunc={val => setSelectedPaymentFrequency(val)} value={selectedPaymentFrequency} options={paymentFrequency} />
        </>
      ) : null}
      {/* Step 3 */}
      {showStep3 && currentStep === 3 && !manualConfirmationPending ? (
        <FamilyMemberStep familyRows={familyRows} addMember={addMember} removeMember={removeMember} />
      ) : null}
      {/* Step 4 */}
      {paymentStepComponent || (
        <>
          {manualConfirmationPending ? (
            <div className="mt-4">
              {manualConfirmationMessage && <p className="mb-3">{manualConfirmationMessage}</p>}
              <div className="flex gap-3">
                <Button text="Cancel" onClick={handleManualConfirmationCancel} disabled={loading} />
                <Button text="Confirm" onClick={handleManualConfirmationConfirm} disabled={loading} />
              </div>
            </div>
          ) : (
            <SidebarFooter
              cancelText={currentStep === 1 ? "Cancel" : "Previous"}
              saveText={getSaveText()}
              isDisabledNext={isDisabled}
              loading={loading}
              isDisabledPrev={loading}
              handleClose={handleCancelAction}
              handleSave={handleSaveAction}
            />
          )}
        </>
      )}
      {/* Hidden PDF container for receipt generation */}
      <div className="absolute -top-[1000%]">
        <div id="plan-receipt-container-pdf" className="hidden">
          {receiptData ? <PlanReceiptPdf data={receiptData} /> : null}
        </div>
      </div>
    </div>
  );
};

export default PlanSelection;
